import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { User, Key, HelpCircle, Search, MessageSquare, Settings, FileText, Lock, Bell } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { userHasVerifiedTokens } from "@/utils/blogUtils";

const navItems = [
  {
    icon: User,
    label: "Profile",
    path: "/profile"
  },
  {
    icon: Key,
    label: "Token",
    path: "/token"
  },
  {
    icon: FileText,
    label: "Blogs",
    path: "/my-blogs",
    requiresToken: true
  },
  {
    icon: Search,
    label: "Found",
    path: "/found"
  },
  {
    icon: Settings,
    label: "Settings",
    path: "/settings"
  },
  {
    icon: HelpCircle,
    label: "Lost",
    path: "/lost"
  },
  {
    icon: MessageSquare,
    label: "Messages",
    path: "/messaging"
  },
  {
    icon: Bell,
    label: "Reminders",
    path: "/reminders"
  },
];

export default function MobileBottomNav() {
  const location = useLocation();
  const { user } = useAuth();
  const [hasToken, setHasToken] = useState<boolean | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    // Force scroll to top with smooth behavior
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    // Also reset the scroll position of the main content
    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.scrollTop = 0;
    }
  }, [location.pathname]);

  // Handle scroll hide/show for blog posts
  useEffect(() => {
    const isBlogPost = location.pathname.includes('/blog/');
    if (!isBlogPost) {
      setIsVisible(true);
      return;
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY < 10) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname, lastScrollY]);

  // Check if user has verified tokens
  useEffect(() => {
    const checkToken = async () => {
      if (!user) {
        setHasToken(false);
        return;
      }
      try {
        const hasTokens = await userHasVerifiedTokens(user.id);
        setHasToken(hasTokens);
      } catch (error) {
        console.error('Error checking tokens:', error);
        setHasToken(false);
      }
    };
    checkToken();
  }, [user]);

  return (
    <nav
      className={cn(
        "md:hidden fixed bottom-3 left-0 right-0 z-50 flex justify-center transition-transform duration-300 pointer-events-none",
        isVisible ? "translate-y-0" : "translate-y-full"
      )}
    >
      <div className="pointer-events-auto bg-card/80 backdrop-blur-md border border-border/80 rounded-xl shadow-lg max-w-md w-[92%]">
        <div className="overflow-x-auto scrollbar-none">
          <div className="flex items-center h-14 min-w-max px-2">
          {navItems.map((item, index) => {
            const isLocked = item.requiresToken && hasToken === false;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={index}
                to={isLocked ? "/token" : item.path}
                onClick={(e) => {
                  if (isLocked) {
                    e.preventDefault();
                  } else {
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                  }
                }}
                className={cn(
                  "flex flex-col items-center justify-center w-[20vw] min-w-[4.5rem] h-[85%] gap-1 px-1",
                  "transition-all duration-200 rounded-xl mx-0.5 relative",
                  isActive && !isLocked
                    ? "bg-primary/20 text-primary font-semibold shadow-[0_2px_8px_-2px_rgba(var(--primary),0.3)]" 
                    : isLocked
                    ? "text-muted-foreground opacity-60 cursor-not-allowed"
                    : "text-foreground hover:bg-primary/10 hover:text-primary"
                )}
                title={isLocked ? "Verify a Metefy token to access blogs" : item.label}
              >
                <div className="relative">
                  <item.icon size={24} className={cn(
                    "transition-colors duration-200",
                    isActive && !isLocked ? "text-primary" : isLocked ? "text-muted-foreground" : "text-foreground"
                  )} />
                  {isLocked && (
                    <Lock size={10} className="absolute -top-1 -right-1 text-muted-foreground" />
                  )}
                </div>
                <span className={cn(
                  "text-xs font-medium",
                  isActive && !isLocked ? "text-primary" : isLocked ? "text-muted-foreground" : "text-foreground"
                )}>{item.label}</span>
              </Link>
              );
            })}
          </div>
        </div>
      </div>
    </nav>
  );
} 